import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Sales Order RPC Contract
 *
 * Provides endpoints for fetching sales orders and their line items.
 */

const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  message: "Must be a numeric string",
});

const UUIDSchema = z.uuid();

/**
 * Sales order status enum
 */
export const SalesOrderStatusSchema = z.enum([
  "pending",
  "in_progress",
  "fulfilled",
  "closed",
]);

/**
 * Sales order sync status enum
 */
export const SalesOrderSyncStatusSchema = z.enum([
  "not_started",
  "pending",
  "in_progress",
  "success",
  "failed",
  "retrying",
]);

/**
 * Pagination schema
 */
export const SalesOrderPaginationSchema = z.object({
  total: z.int().nonnegative(),
  limit: z.int().positive(),
  offset: z.int().nonnegative(),
});

/**
 * Customer summary for sales order
 */
export const SalesOrderCustomerSummarySchema = z.object({
  id: UUIDSchema,
  name: z.string().nullable(),
  customerNumber: z.string().nullable().optional(),
});

/**
 * Shipment summary for sales order
 */
export const SalesOrderShipmentSummarySchema = z.object({
  id: UUIDSchema,
  originLoadNumber: z.string().nullable(),
  status: z.string().nullable(),
  origin: z.string().nullable(),
  destination: z.string().nullable(),
});

/**
 * Sales order line item schema
 */
export const SalesOrderLineItemSchema = z.object({
  id: UUIDSchema,
  lineItemNumber: z.int().nullable().optional(),
  quantity: z.number().nullable().optional(),
  unitPrice: DecimalStringSchema.nullable().optional(),
  lineTotal: DecimalStringSchema,
  productId: UUIDSchema.nullable().optional(),
  productVariantId: UUIDSchema.nullable().optional(),
  customerProductId: UUIDSchema.nullable().optional(),
  rescheduledDate: z.string().nullable().optional(),
  // Product details if available
  productName: z.string().nullable().optional(),
  productSku: z.string().nullable().optional(),
  customerProductSku: z.string().nullable().optional(),
});

/**
 * Base sales order schema
 */
export const SalesOrderSchema = z.object({
  id: UUIDSchema,
  internalId: z.int(),
  organizationId: UUIDSchema,
  customerId: UUIDSchema,
  shipmentId: UUIDSchema.nullable().optional(),
  source: z.string(),
  orderDate: z.string(),
  deliveryDate: z.string().nullable().optional(),
  shipDate: z.string().nullable().optional(),
  customerPurchaseOrderNumber: z.string().nullable().optional(),
  status: SalesOrderStatusSchema,
  totalAmount: DecimalStringSchema.nullable().optional(),
  currencyCode: z.string().nullable().optional(),
  originId: z.string().nullable().optional(),
  originSystem: z.string().nullable().optional(),
  originEntity: z.string().nullable().optional(),
  originSyncStatus: SalesOrderSyncStatusSchema.nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * List sales orders input
 */
export const ListSalesOrdersInputSchema = z.object({
  customerId: UUIDSchema.optional(),
  shipmentId: UUIDSchema.optional(),
  status: z.array(SalesOrderStatusSchema).optional(),
  orderDateFrom: z.string().optional(),
  orderDateTo: z.string().optional(),
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

/**
 * List sales orders response
 */
export const ListSalesOrdersResponseSchema = z.object({
  salesOrders: z.array(
    SalesOrderSchema.extend({
      customer: SalesOrderCustomerSummarySchema.nullable().optional(),
    }),
  ),
  pagination: SalesOrderPaginationSchema,
});

/**
 * Get sales order input
 */
export const GetSalesOrderInputSchema = z.object({
  id: UUIDSchema,
});

/**
 * Get sales order response
 */
export const GetSalesOrderResponseSchema = z.object({
  salesOrder: SalesOrderSchema.extend({
    customer: SalesOrderCustomerSummarySchema.nullable().optional(),
    shipment: SalesOrderShipmentSummarySchema.nullable().optional(),
  }).nullable(),
  lineItems: z.array(SalesOrderLineItemSchema),
});

/**
 * Sales Orders RPC Contract
 */
export const salesOrdersRpcContracts = {
  get: oc
    .input(GetSalesOrderInputSchema)
    .output(GetSalesOrderResponseSchema)
    .route({
      method: "GET",
    }),
  list: oc
    .input(ListSalesOrdersInputSchema)
    .output(ListSalesOrdersResponseSchema)
    .route({
      method: "GET",
    }),
} as const;

// Type exports
export type SalesOrder = z.infer<typeof SalesOrderSchema>;
export type SalesOrderLineItem = z.infer<typeof SalesOrderLineItemSchema>;
export type SalesOrderCustomerSummary = z.infer<
  typeof SalesOrderCustomerSummarySchema
>;
export type SalesOrderShipmentSummary = z.infer<
  typeof SalesOrderShipmentSummarySchema
>;
export type ListSalesOrdersInput = z.infer<typeof ListSalesOrdersInputSchema>;
export type ListSalesOrdersResponse = z.infer<
  typeof ListSalesOrdersResponseSchema
>;
export type GetSalesOrderInput = z.infer<typeof GetSalesOrderInputSchema>;
export type GetSalesOrderResponse = z.infer<typeof GetSalesOrderResponseSchema>;
export type SalesOrderStatus = z.infer<typeof SalesOrderStatusSchema>;
